// src/components/common/PaginationBottom.tsx
import React, { useMemo } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NavigateBefore, NavigateNext, MoreHoriz } from '@mui/icons-material';
import { colors } from '../../theme/theme';

interface PaginationBottomProps {
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  beingType: 'clients' | 'pets' | 'bookings' | 'quotations';
  activeFilter: string | null;
  textFilter: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (pageNumber: number) => void;
}

// Styled components
const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '1rem 0.25rem 1.2rem 0.25rem',
  borderTop: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.light,
  marginBottom: 0,

  '@media (max-width: 1206px)': {
    flexDirection: 'column',
    padding: '0 0.05rem 1rem 0.05rem',
  },
  '@media (max-width: 600px)': {},
}));

const PageInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  textAlign: 'center',
  fontSize: '2.25rem',
  color:
    theme.palette.mode === 'dark' ? colors.text.light : colors.primary.dark,
  userSelect: 'none',
  fontWeight: 'bold',
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
}));

const FilterText = styled(Typography)(({ theme }) => ({
  fontSize: '2.25rem',
  color: theme.palette.primary.main,
  fontStyle: 'italic',
  userSelect: 'none',
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.8rem',
  flexWrap: 'wrap',
  padding: 0,
  marginTop: '0.5rem',
  '@media (max-width: 600px)': {
    gap: '0.25rem',
  },
}));

const NavigationButton = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '3rem',
  height: '3rem',
  position: 'relative',
  '& .MuiSvgIcon-root': {
    fontSize: '6rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
    margin: 0,
    padding: 0,
  },
  '&:hover:not(.disabled) .MuiSvgIcon-root': {
    scale: 1.3,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.primary.main,
  },
  '&.disabled': {
    color: theme.palette.primary.light,
  },
  '&.disabled .MuiSvgIcon-root': {
    color: theme.palette.divider,
    cursor: 'default',
  },
  '@media (max-width: 600px)': {
    '& .MuiSvgIcon-root': {
      fontSize: '6rem',
    },
  },
}));

// const CircularPageButton = styled(Button, {
//   shouldForwardProp: (prop) => prop !== '$isActive',
// })<{ $isActive: boolean }>(({ theme, $isActive }) => ({
//   minWidth: '3.5rem',
//   minHeight: '3.5rem',
//   width: '3.5rem',
//   height: '3.5rem',
//   borderRadius: '50%',
//   padding: 0,
//   fontSize: '2rem',
//   fontWeight: 'bold',
//   backgroundColor: $isActive ? theme.palette.secondary.main : 'transparent',
//   color: $isActive
//     ? theme.palette.secondary.contrastText
//     : theme.palette.mode === 'dark'
//       ? theme.palette.secondary.light
//       : theme.palette.primary.main,
//   border: $isActive
//     ? 'none'
//     : theme.palette.mode === 'dark'
//       ? `2px solid ${theme.palette.secondary.light}`
//       : `2px solid ${theme.palette.primary.main}`,
//   cursor: 'pointer',
//   transition: 'all 0.2s ease',
//   display: 'inline-flex',
//   justifyContent: 'center',
//   alignItems: 'center',
//   '&:hover': {
//     backgroundColor: $isActive
//       ? theme.palette.secondary.main
//       : theme.palette.primary.main,
//     color: $isActive
//       ? theme.palette.secondary.contrastText
//       : theme.palette.primary.contrastText,
//     borderColor: theme.palette.primary.main,
//   },
//   '@media (max-width: 600px)': {
//     minWidth: '2.75rem',
//     minHeight: '2.75rem',
//     width: '2.75rem',
//     height: '2.75rem',
//     fontSize: '1.9rem',
//     margin: '0 0.06rem',
//   },
// }));

const CirclePageButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '3.3rem',
  minHeight: '2.9rem',
  borderRadius: '50%',
  fontSize: '1.8rem',
  fontWeight: 700,
  cursor: isActive ? 'default' : 'pointer',
  border: `2px solid`,
  borderColor: isActive
    ? theme.palette.mode === 'dark'
      ? colors.secondary.light
      : theme.palette.primary.dark
    : theme.palette.mode === 'dark'
      ? '#fff'
      : theme.palette.primary.dark,
  backgroundColor: isActive
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.dark
    : theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : theme.palette.grey[50],
  color: isActive
    ? theme.palette.mode === 'dark'
      ? colors.primary.dark
      : theme.palette.grey[50]
    : theme.palette.mode === 'dark'
      ? '#fff'
      : theme.palette.primary.dark,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.primary.dark
      : theme.palette.mode === 'dark'
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
    color: isActive
      ? theme.palette.mode === 'dark'
        ? colors.primary.dark
        : theme.palette.grey[50]
      : theme.palette.mode === 'dark'
        ? '#fff'
        : theme.palette.grey[50],
    transform: isActive ? 'none' : 'scale(1.2)',
    transition: 'all 0.2s ease',
  },

  '@media (max-width: 600px)': {
    minWidth: '3rem',
    minHeight: '2.7rem',
  },
}));

const EllipsisBox = styled(Box)(({ theme }) => ({
  minWidth: '3rem',
  minHeight: '3rem',
  width: '3rem',
  height: '3rem',
  padding: 0,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.primary.main,
  backgroundColor: 'transparent',
  cursor: 'default',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  '& .MuiSvgIcon-root': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.primary.main,
    fontSize: '2.5rem',
  },
  '@media (max-width: 600px)': {
    minWidth: '2.5rem',
    minHeight: '2.5rem',
    width: '2rem',
    height: '2.5rem',
    margin: 0,
    '& .MuiSvgIcon-root': {
      fontSize: '2rem',
    },
  },
}));

const generatePageButtons = (
  currentPage: number,
  totalPages: number,
  isMobile: boolean,
  isWide: boolean
): (number | string)[] => {
  const maxPagesToShow = isMobile ? 5 : isWide ? 18 : 7;
  const firstPageCount = isMobile ? 2 : isWide ? 5 : 3;
  const lastPageCount = isMobile ? 2 : isWide ? 5 : 3;

  if (totalPages <= maxPagesToShow) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const buttons: (number | string)[] = [];

  for (let i = 0; i < firstPageCount; i++) {
    buttons.push(i + 1);
  }

  buttons.push('...');

  for (let i = 0; i < lastPageCount; i++) {
    buttons.push(totalPages - (lastPageCount - 1 - i));
  }

  return buttons;
};

const PaginationBottom: React.FC<PaginationBottomProps> = ({
  pageNumber,
  totalPages,
  totalCount,
  pageSize,
  beingType,
  activeFilter,
  textFilter,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
}) => {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isWide = useMediaQuery('(min-width: 1500px)');

  const pageButtons = useMemo(
    () => generatePageButtons(pageNumber, totalPages, isMobile, isWide),
    [pageNumber, totalPages, isMobile, isWide]
  );

  // Build filter description
  const filterDescription = useMemo(() => {
    if (!activeFilter && !textFilter) {
      return null;
    }

    const parts = [];
    if (activeFilter) {
      parts.push(`"${activeFilter}"`);
    }
    if (textFilter) {
      parts.push(`"${textFilter}"`);
    }

    return parts.join(', ');
  }, [activeFilter, textFilter]);

  // Calculate range for current page
  const startItem = (pageNumber - 1) * pageSize + 1;
  const endItem = Math.min(pageNumber * pageSize, totalCount);

  const itemLabel =
    beingType === 'clients'
      ? 'Clients'
      : beingType === 'pets'
        ? 'Pets'
        : beingType === 'quotations'
          ? 'Quotations'
          : 'Bookings';

  const handlePageButtonClick = (page: number | string) => {
    if (typeof page === 'number') {
      onPageChange(page);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      onPageChange(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      onPageChange(pageNumber + 1);
    }
  };

  return (
    <PaginationContainer>
      {/* ONLY 1 */}
      {totalPages === 1 && (
        <PageInfoContainer
          sx={{ '@media (min-width: 601px)': { display: 'none' } }}
        >
          {totalCount} {itemLabel} found
          {filterDescription && ' for '}
          {filterDescription && (
            <FilterText>&nbsp;{filterDescription}</FilterText>
          )}
        </PageInfoContainer>
      )}

      {totalPages > 1 && (
        <PageInfoContainer
          sx={{ '@media (min-width: 601px)': { display: 'none' } }}
        >
          {startItem} - {endItem} of {totalCount}&nbsp;{itemLabel}
        </PageInfoContainer>
      )}

      {/* Only show if multiple pages */}
      {totalPages > 1 && (
        <>
          {/* Row: Page Buttons Container */}
          <ButtonsContainer>
            {/* Previous Button */}
            <NavigationButton
              onClick={handlePreviousPage}
              className={!hasPreviousPage ? 'disabled' : ''}
              role="button"
              tabIndex={!hasPreviousPage ? -1 : 0}
              aria-label="Previous page"
            >
              <NavigateBefore />
            </NavigationButton>

            {/* Page Number Buttons */}
            {pageButtons.map((page, index) =>
              page === '...' ? (
                <EllipsisBox key={`ellipsis-${index}`}>
                  <MoreHoriz />
                </EllipsisBox>
              ) : (
                <CirclePageButton
                  key={`page-${page}`}
                  isActive={page === pageNumber}
                  onClick={() => handlePageButtonClick(page)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === pageNumber ? 'page' : undefined}
                >
                  {page}
                </CirclePageButton>
              )
            )}

            {/* Next Button */}
            <NavigationButton
              onClick={handleNextPage}
              className={!hasNextPage ? 'disabled' : ''}
              role="button"
              tabIndex={!hasNextPage ? -1 : 0}
              aria-label="Next page"
            >
              <NavigateNext />
            </NavigationButton>
          </ButtonsContainer>
        </>
      )}
    </PaginationContainer>
  );
};

export default PaginationBottom;
